'use client'

import {
  BeautyFrame,
  BeautyKicker,
  FullBleedImage,
  SafeText,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/offer_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/offer_02.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/offer_03.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/offer_04.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/offer_05.jpg',
]

const total = 5
const serif = '"Cormorant Garamond", "Georgia", serif'
const sans = '"Avenir Next", "Helvetica Neue", Arial, sans-serif'

const fallbacks = [
  {
    eyebrow: 'Luxury skincare offer',
    headline: 'Tu ritual de glow empieza hoy',
    subheadline:
      'Una experiencia premium para cuidar tu piel con intención, calma y resultados visibles.',
    offer: 'Ritual completo de cuidado',
    proof: 'Resultados visibles desde la primera rutina',
    urgency: 'Disponible esta semana',
    cta: 'Reservar mi ritual',
    bullets: ['Limpieza inteligente', 'Hidratación profunda', 'Glow natural'],
    offerBox: {
      offer: 'Ritual completo de cuidado',
      price: 'Oferta especial',
      compareAt: 'Valor completo',
      bonus: 'Detalle premium incluido',
      urgency: 'Disponible esta semana',
      cta: 'Reservar mi ritual',
    },
  },
  {
    eyebrow: 'Lo que incluye',
    headline: 'Todo para una piel más luminosa',
    subheadline: 'Una rutina curada para elevar tu cuidado diario.',
    offer: 'Ritual completo de cuidado',
    body: 'Un sistema simple, sensorial y elegante para cuidar tu piel sin saturarla.',
    bullets: ['Diagnóstico', 'Rutina', 'Seguimiento'],
    offerBox: {
      offer: 'Ritual completo de cuidado',
      bonus: 'Acompañamiento de rutina',
      cta: 'Ver detalles',
    },
  },
  {
    eyebrow: 'Por qué funciona',
    headline: 'La rutina se siente premium. El resultado se ve natural.',
    subheadline: 'El lujo real está en una rutina que puedes sostener.',
    body: 'Una experiencia pensada para aportar calma, hidratación y luminosidad visible.',
    proof: 'Resultados visibles desde la primera rutina',
    bullets: ['Glow', 'Hidratación', 'Calma'],
  },
  {
    eyebrow: 'Bonificación',
    headline: 'Un detalle de lujo, solo por tiempo limitado.',
    subheadline: 'Ideal para quien quiere una rutina elegante, simple y efectiva.',
    body: 'Una oportunidad para empezar con más claridad, mejor experiencia y una rutina lista para usar.',
    urgency: 'Disponible esta semana',
    offerBox: {
      bonus: 'Detalle premium incluido',
      urgency: 'Disponible esta semana',
      cta: 'Aprovechar ahora',
    },
  },
  {
    eyebrow: 'Último paso',
    headline: 'Reserva tu ritual',
    subheadline: 'Empieza hoy con una rutina beauty más clara, elegante y efectiva.',
    result: 'Tu piel merece un cuidado que se sienta tan bien como se ve.',
    cta: 'Empezar ahora',
    offerBox: {
      offer: 'Ritual completo de cuidado',
      price: 'Oferta especial',
      compareAt: 'Valor completo',
      cta: 'Empezar ahora',
    },
  },
]

export default function BeautyLuxuryOfferPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#B18A4A')
  const accent = design.accent || '#B18A4A'

  const cream = '#F6EEE3'
  const ink = '#211815'
  const muted = 'rgba(33,24,21,0.62)'
  const eyebrowClass = 'text-[12px] font-bold uppercase tracking-[0.22em]'

  const copy = getBeautyCopy(draft, index, fallbacks[index])

  const offerBox = (copy as any).offerBox || {}
  const offerPrice = offerBox.price || ''
  const offerCompareAt = offerBox.compareAt || offerBox.compare_at || ''
  const offerBonus = offerBox.bonus || ''
  const offerCta = offerBox.cta || copy.cta || 'Empezar ahora'

  const offerItems =
    copy.bullets?.length
      ? copy.bullets
      : offerBonus
        ? [offerBonus]
        : fallbacks[index].bullets || []

  const proofTags =
    copy.bullets?.length
      ? copy.bullets
      : ['Glow', 'Hidratación', 'Calma']

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav={index === 0 || index === 4 ? 'light' : 'dark'}
      className="bg-[#F6EEE3]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[0], {
              overlay: 'black',
              opacity: 0.22,
            })}
          />

          <div className="absolute inset-0 bg-gradient-to-r from-black/62 via-black/18 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/30 to-transparent" />

          <div className="absolute left-8 top-8 z-10">
            <BeautyKicker
              color="#fff"
              borderColor="rgba(255,255,255,0.32)"
              background="rgba(255,255,255,0.16)"
            >
              {copy.eyebrow}
            </BeautyKicker>
          </div>

          <div className="absolute bottom-16 left-8 z-10 max-w-[82%] text-white">
            <SafeText
              className="text-[clamp(46px,11vw,76px)] font-normal leading-[0.84] tracking-[-0.055em]"
              style={{ fontFamily: serif }}
            >
              {copy.headline}
            </SafeText>

            <SafeText
              className="mt-5 max-w-[86%] text-[15px] leading-[1.45] text-white/82"
              style={{ fontFamily: sans }}
            >
              {copy.subheadline || copy.body || copy.offer}
            </SafeText>

            <div
              className="mt-7 inline-flex rounded-full px-5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white"
              style={{ background: accent, fontFamily: sans }}
            >
              {copy.cta || offerCta}
            </div>
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <>
          <div className="absolute inset-0" style={{ background: cream }} />

          <div className="absolute left-0 top-0 h-full w-[38%] overflow-hidden">
            <FullBleedImage
              {...getBeautyVisual(draft, index, bg[1], {
                overlay: 'white',
                opacity: 0.08,
              })}
            />
          </div>

          <div
            className="absolute left-[38%] top-0 h-full w-px"
            style={{ background: 'rgba(33,24,21,0.12)' }}
          />

          <div className="absolute right-8 top-10 max-w-[50%]" style={{ color: ink }}>
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: accent, fontFamily: sans }}
            >
              {copy.eyebrow || 'Lo que incluye'}
            </div>

            <SafeText
              className="mt-5 text-[46px] font-normal leading-[0.92] tracking-[-0.04em]"
              style={{ fontFamily: serif }}
            >
              {copy.offer || offerBox.offer || copy.headline || 'Todo para una piel más luminosa'}
            </SafeText>

            {(copy.subheadline || copy.body) ? (
              <SafeText
                className="mt-5 text-[14px] leading-[1.42]"
                style={{ color: muted, fontFamily: sans }}
              >
                {copy.subheadline || copy.body}
              </SafeText>
            ) : null}

            <div className="mt-8 space-y-4">
              {(offerItems.length ? offerItems : ['Diagnóstico', 'Rutina', 'Seguimiento'])
                .slice(0, 3)
                .map((item: string, idx: number) => (
                  <div
                    key={`${idx}-${item}`}
                    className="flex items-start gap-4 border-t pt-4"
                    style={{ borderColor: 'rgba(33,24,21,0.12)' }}
                  >
                    <div
                      className="text-[12px] font-bold"
                      style={{ color: accent, fontFamily: sans }}
                    >
                      0{idx + 1}
                    </div>

                    <SafeText
                      className="text-[17px] leading-[1.2]"
                      style={{ fontFamily: serif }}
                    >
                      {item}
                    </SafeText>
                  </div>
                ))}
            </div>
          </div>
        </>
      ) : null}

      {index === 2 ? (
        <>
          <div className="absolute inset-0" style={{ background: '#FFF8F0' }} />

          <div
            className="absolute -right-20 -top-20 h-[72%] w-[72%] rounded-full"
            style={{ background: `${accent}22` }}
          />

          <div className="absolute bottom-0 right-0 h-[58%] w-[54%] overflow-hidden rounded-tl-[44px]">
            <FullBleedImage
              {...getBeautyVisual(draft, index, bg[2], {
                overlay: 'white',
                opacity: 0.04,
              })}
            />
          </div>

          <div className="absolute left-9 top-10 max-w-[62%]" style={{ color: ink }}>
            <div
              className={eyebrowClass}
              style={{ color: accent, fontFamily: sans }}
            >
              {copy.eyebrow || 'Por qué funciona'}
            </div>

            <SafeText
              className="mt-5 text-[52px] font-normal leading-[0.9] tracking-[-0.05em]"
              style={{ fontFamily: serif }}
            >
              {copy.proof || copy.headline || 'La rutina se siente premium. El resultado se ve natural.'}
            </SafeText>

            <SafeText
              className="mt-6 max-w-[82%] text-[15px] leading-[1.5]"
              style={{ color: muted, fontFamily: sans }}
            >
              {copy.body || copy.subheadline || copy.result}
            </SafeText>
          </div>

          <div className="absolute left-9 top-[51%] z-10 flex w-[52%] flex-col gap-3">
            {proofTags.slice(0, 3).map((item: string) => (
              <div
                key={item}
                className="w-fit rounded-[14px] border px-4 py-2.5 text-[12px] font-semibold leading-[1.2]"
                style={{
                  borderColor: `${accent}55`,
                  color: ink,
                  background: 'rgba(255,255,255,0.46)',
                  fontFamily: sans,
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {index === 3 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[3], {
              overlay: 'white',
              opacity: 0.04,
            })}
            objectPosition="74% center"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#F6EEE3]/98 via-[#F6EEE3]/88 via-[58%] to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F6EEE3]/72 via-transparent to-transparent" />

          <div className="absolute left-8 top-10 z-10 max-w-[58%]" style={{ color: ink }}>
            <div
              className={eyebrowClass}
              style={{ color: accent, fontFamily: sans }}
            >
              {copy.eyebrow || 'Bonificación'}
            </div>

            <SafeText
              className="mt-6 text-[52px] font-normal leading-[0.9] tracking-[-0.05em]"
              style={{ fontFamily: serif }}
            >
              {copy.headline || copy.urgency || offerBox.urgency || 'Un detalle de lujo, solo por tiempo limitado.'}
            </SafeText>

            <SafeText
              className="mt-6 max-w-[92%] text-[18px] leading-[1.48]"
              style={{ color: 'rgba(33,24,21,0.78)', fontFamily: sans }}
            >
              {copy.subheadline ||
                copy.body ||
                offerBonus ||
                'Ideal para quien quiere una rutina elegante, simple y efectiva sin saturar su piel.'}
            </SafeText>

            {(copy.urgency || offerBox.urgency) ? (
              <div
                className="mt-8 w-fit rounded-[14px] border px-4 py-3 text-[12px] font-bold uppercase tracking-[0.14em]"
                style={{
                  borderColor: `${accent}55`,
                  color: accent,
                  background: 'rgba(255,255,255,0.48)',
                  fontFamily: sans,
                }}
              >
                {copy.urgency || offerBox.urgency}
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {index === 4 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[4], {
              overlay: 'black',
              opacity: 0.42,
            })}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/12 to-transparent" />

          <div className="absolute bottom-16 left-8 right-8 text-white">
            <div
              className="text-[12px] font-bold uppercase tracking-[0.24em] text-white/70"
              style={{ fontFamily: sans }}
            >
              {copy.eyebrow || 'Último paso'}
            </div>

            <SafeText
              className="mt-4 max-w-[78%] text-[56px] font-normal leading-[0.88] tracking-[-0.05em]"
              style={{ fontFamily: serif }}
            >
              {copy.headline || copy.cta || copy.offer || 'Reserva tu ritual'}
            </SafeText>

            <div className="mt-7 flex items-end justify-between">
              <div
                className="max-w-[48%] text-[15px] leading-[1.4] text-white/75"
                style={{ fontFamily: sans }}
              >
                {copy.subheadline || copy.result || copy.body || copy.offer || offerBox.offer}
              </div>

              <div
                className="rounded-full px-6 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white"
                style={{ background: accent, fontFamily: sans }}
              >
                {offerCta}
              </div>
            </div>

            {(offerPrice || offerCompareAt) ? (
              <div className="mt-5 flex items-center gap-3 text-white">
                {offerCompareAt ? (
                  <span
                    className="text-[14px] line-through opacity-60"
                    style={{ fontFamily: sans }}
                  >
                    {offerCompareAt}
                  </span>
                ) : null}

                {offerPrice ? (
                  <span
                    className="text-[20px] font-semibold"
                    style={{ fontFamily: sans }}
                  >
                    {offerPrice}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </BeautyFrame>
  )
}